/**
 * @description Handles JS errors that occur within the child elements by catching them and displaying an error banner. This
 *  prevents the error from continuing to bubble up to the top of the application and potentially break the page.
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';

class ErrorBoundary extends React.Component {
  static styles = {
    paragraph: {
      alignItems: 'center',
      display: 'flex'
    },
    icon: {
      marginRight: '8px'
    }
  }

  /**
   * Sets the error state based on errors that occured
   * @returns {object} new state
   */
  static getDerivedStateFromError () {
    return { hasError: true }
  }

  constructor (props) {
    super(props);

    this.state = {
      // Flag that indicates that an error occured
      hasError: false
    };
  }

  /**
   * Renders the error content based on the fallback or variant indicated
   * @returns error banner
   */
  renderError () {
    // If fallback content was indicated serve that first
    if (this.props.fallback) {
      return this.props.fallback;
    }

    // Based on the variant indicated, serve the appropriate error content
    switch (this.props.variant) {
      case 'none':
        return null;
      default:
        return (
          <Box>
            <Typography sx={ErrorBoundary.styles.paragraph}>
              <ErrorIcon color='error' sx={ErrorBoundary.styles.icon} />An error occured
            </Typography>
          </Box>
        )
    }
  }

  render () {
    // If an error occured in the child elements, render the error content instead
    if (this.state.hasError) {
      return this.renderError();
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired,
  fallback: PropTypes.element,
  variant: PropTypes.string
};

ErrorBoundary.defaultProps = {
  fallback: undefined,
  variant: 'block'
}

export default ErrorBoundary;
